import React, { useState } from 'react';
import { Container, Row, Col, Card, Alert, Form, Button } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { Formik } from 'formik';
import * as Yup from 'yup';

const validationSchema = Yup.object({
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
});

const ResetPasswordPage = () => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get token from URL query params
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError(null);
      setSuccess(false);
      
      if (!token) {
        setError('Invalid or missing reset token');
        setSubmitting(false);
        return;
      }
      
      await authService.resetPassword(token, { password: values.password });
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <Container>
        <Row className="justify-content-md-center">
          <Col md={6}>
            <Alert variant="danger" className="mt-5">
              <Alert.Heading>Invalid Reset Link</Alert.Heading>
              <p>The password reset link is invalid or has expired.</p>
              <hr />
              <Link to="/forgot-password">
                <Button variant="outline-danger">Request New Reset Link</Button>
              </Link>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container>
      <Row className="justify-content-md-center">
        <Col md={6}>
          <Card className="p-4 shadow mt-5">
            <Card.Body>
              <h2 className="text-center mb-4">Reset Password</h2>
              
              {error && <Alert variant="danger">{error}</Alert>}
              
              {success ? (
                <Alert variant="success">
                  <Alert.Heading>Password Reset Successful!</Alert.Heading>
                  <p>Your password has been reset successfully.</p>
                  <hr />
                  <p className="mb-0">
                    Redirecting to login page in 3 seconds... or <Link to="/login">click here</Link>
                  </p>
                </Alert>
              ) : (
                <>
                  <p className="text-muted text-center mb-4">
                    Enter your new password below.
                  </p>
                  
                  <Formik
                    initialValues={{ password: '', confirmPassword: '' }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                      <Form noValidate onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                          <Form.Label>New Password *</Form.Label>
                          <Form.Control
                            type="password"
                            name="password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.password && errors.password}
                            placeholder="Enter new password"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.password}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted">
                            Must be at least 6 characters
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Confirm Password *</Form.Label>
                          <Form.Control
                            type="password"
                            name="confirmPassword"
                            value={values.confirmPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.confirmPassword && errors.confirmPassword}
                            placeholder="Confirm new password"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.confirmPassword}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Button 
                          type="submit" 
                          variant="primary" 
                          className="w-100 mb-3" 
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                        </Button>
                      </Form>
                    )}
                  </Formik>
                  
                  <div className="text-center mt-3">
                    <Link to="/login" className="text-muted small">
                      Back to Login
                    </Link>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPasswordPage;
